/**
 * latest app 専用 color tokens (= Notion 28 spec canonical).
 *
 * 一次情報: docs/design-system/03_ui_implementation_spec.md §1.1
 * 運用規則:
 *  - 純黒 #000 / 純白 on body は使わない (本文は fg.default = #373530)
 *  - accent.primary 以外の彩度の高い色は「状態を示す」ときのみ
 *  - 罫線 (border.default) は最後の手段。原則は bg.subtle/muted で領域分割
 *
 * apps/latest/ 配下のみ参照可. apps/legacy/ からは参照不可 (= R2 物理分離).
 */
export const colorTokens = {
  // === Neutral (Light Mode) ===
  bg: {
    default: '#FFFFFF',
    subtle: '#F7F7F5',
    muted: '#F1F1EF',
    emphasis: '#EDEDEC',
  },
  border: {
    default: '#E9E9E7',
    // 半透明罫線 (Notion 流). 純粋な #E9E9E7 より背景に馴染む.
    subtle: 'rgba(55,53,47,0.09)',
    faint: 'rgba(55,53,47,0.06)',
  },
  // hover/active 用の半透明 overlay (Notion 流 chrome).
  overlay: {
    soft: 'rgba(55,53,47,0.03)',
    hover: 'rgba(55,53,47,0.04)',
    selected: 'rgba(55,53,47,0.06)',
    active: 'rgba(55,53,47,0.08)',
    placeholderIcon: 'rgba(55,53,47,0.2)',
  },
  fg: {
    default: '#373530',
    // muted / faint は共有 spec (#787774 / #A6A299) より 1 段暗い kakeru 逸脱値
    // (ADR-0020): 年配薬剤師の終日利用で 12px 帯の二次テキストが淡すぎるため、
    // bg.subtle 上でも muted は AA (4.5:1) を満たす濃度にする。階層構造は不変。
    muted: '#6B6A66',
    faint: '#8F8C85',
    disabled: '#C7C5BF',
  },

  // === Accent (CTA / Link / Focus) ===
  accent: {
    primary: '#2383E2',
    primaryHover: '#1A6BB8',
    primaryFg: '#FFFFFF',
    ring: '#2383E2',
  },

  // === Semantic State ===
  state: {
    infoBg: '#E9F3F7',
    infoFg: '#487CA5',
    warnBg: '#FAF3DD',
    warnFg: '#C29343',
    warnIcon: '#CB912F',
    dangerBg: '#FAECEC',
    dangerFg: '#C4554D',
    dangerIcon: '#D44C47',
    successBg: '#DDEDDF',
    successFg: '#448361',
    recordingIcon: '#D44C47',
  },

  // === Focus Ring (composed) ===
  focus: {
    // 入力欄・primary CTA 等、 フォーカスを目立たせたい要素用 (accent blue)。
    ringShadow: '0 0 0 4px rgba(35,131,226,0.25)',
    // 静かな chrome (トグル等、 active 状態が別途分かる要素) 用の humble なグレーリング。
    // warm-black (fg base) ベースで、 青アクセントを主張しない。
    ringNeutral: '0 0 0 2px rgba(55,53,47,0.20)',
  },

  // === Shadows (Notion 流) ===
  shadow: {
    cardSubtle: '0 1px 2px rgba(15,15,15,0.04)',
    popover:
      '0 0 0 1px rgba(15,15,15,0.05), 0 3px 6px rgba(15,15,15,0.10), 0 9px 24px rgba(15,15,15,0.20)',
    modal: '0 0 0 1px rgba(15,15,15,0.05), 0 12px 36px rgba(15,15,15,0.30)',
  },

  // === Dark Mode (Toast 等の dark surface) ===
  dark: {
    bgDefault: '#191919',
    bgSubtle: '#1F1F1F',
    bgMuted: '#252525',
    fgDefault: '#D4D4D4',
    fgMuted: '#9B9B9B',
  },
} as const

export type ColorTokens = typeof colorTokens
